import { z } from 'zod';
import { ModuleTypeEnum } from './module-type';
import { FieldTypeEnum } from './custom-field.types';

/**
 * JoinSpec — the standard way to describe how two records are connected,
 * shared by calculated LOOKUP/ROLLUP fields and (eventually) info-panel
 * related-record tables. One join language across the system.
 *
 *  - foreignKey: follow a scalar FK column on the source record
 *                (e.g. invoice.entityId → Entity)
 *  - reference:  follow a REFERENCE custom field on the source record
 *                (stored value is { type, id, name })
 *  - match:      value-equality join — source.localField = target.targetField
 *  - reverse:    records on the target module that point back at the source
 *                record (one-to-many; used by ROLLUP, not LOOKUP)
 */

export const ForeignKeyJoinSchema = z.object({
  type: z.literal('foreignKey'),
  /** Scalar column on the source record holding the target id (e.g. "entityId") */
  localField: z.string().min(1),
  targetModule: z.nativeEnum(ModuleTypeEnum),
});

export const ReferenceJoinSchema = z.object({
  type: z.literal('reference'),
  /** fieldName of the REFERENCE custom field on the source module */
  fieldName: z.string().min(1),
  targetModule: z.nativeEnum(ModuleTypeEnum),
});

export const MatchJoinSchema = z.object({
  type: z.literal('match'),
  /** Field on the source record whose value must equal target.targetField */
  localField: z.string().min(1),
  targetModule: z.nativeEnum(ModuleTypeEnum),
  /** Field on the target module compared against localField's value */
  targetField: z.string().min(1),
});

export const ReverseJoinSchema = z.object({
  type: z.literal('reverse'),
  targetModule: z.nativeEnum(ModuleTypeEnum),
  /** Field on the target module that holds the source record's id */
  targetField: z.string().min(1),
});

export const JoinSpecSchema = z.discriminatedUnion('type', [
  ForeignKeyJoinSchema,
  ReferenceJoinSchema,
  MatchJoinSchema,
  ReverseJoinSchema,
]);

export type ForeignKeyJoin = z.infer<typeof ForeignKeyJoinSchema>;
export type ReferenceJoin = z.infer<typeof ReferenceJoinSchema>;
export type MatchJoin = z.infer<typeof MatchJoinSchema>;
export type ReverseJoin = z.infer<typeof ReverseJoinSchema>;
export type JoinSpec = z.infer<typeof JoinSpecSchema>;

/** To-one joins usable by LOOKUP fields (reverse is to-many → ROLLUP) */
export const LookupJoinSchema = z.discriminatedUnion('type', [
  ForeignKeyJoinSchema,
  ReferenceJoinSchema,
  MatchJoinSchema,
]);
export type LookupJoin = z.infer<typeof LookupJoinSchema>;

/**
 * Stored in CustomFieldDefinition.settings.lookup for LOOKUP fields.
 * The value is resolved server-side per page of rows (batched, org-scoped)
 * and attached to rows exactly like stored custom field values.
 */
export const LookupSettingsSchema = z.object({
  join: LookupJoinSchema,
  /** Field on the target module whose value is shown (column name or custom fieldName) */
  targetField: z.string().min(1),
  /** True when targetField is a custom field on the target module */
  targetFieldIsCustom: z.boolean().optional(),
  /**
   * Display type of the looked-up value, captured when the field is defined
   * so the client can render without loading the target module's fields.
   */
  resultFieldType: FieldTypeEnum,
});
export type LookupSettings = z.infer<typeof LookupSettingsSchema>;

/** Aggregations supported by ROLLUP fields */
export const RollupAggregateEnum = z.enum(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX']);
export type RollupAggregate = z.infer<typeof RollupAggregateEnum>;

/**
 * Stored in CustomFieldDefinition.settings.rollup for ROLLUP fields.
 * Aggregates records on the target module that point back at the source
 * record (reverse join). Resolved server-side alongside lookups.
 */
export const RollupSettingsSchema = z.object({
  join: ReverseJoinSchema,
  aggregate: RollupAggregateEnum,
  /**
   * Field on the target module to aggregate (column name or custom
   * fieldName). Not required for COUNT.
   */
  targetField: z.string().min(1).optional(),
  /** True when targetField is a custom field on the target module */
  targetFieldIsCustom: z.boolean().optional(),
  /**
   * Display type of the aggregate (NUMBER for COUNT/AVG, CURRENCY when
   * summing money, DATE for MIN/MAX over dates, ...).
   */
  resultFieldType: FieldTypeEnum,
});
export type RollupSettings = z.infer<typeof RollupSettingsSchema>;

export interface ForeignKeyOption {
  localField: string;
  targetModule: ModuleTypeEnum;
  label: string;
}

export interface ReverseJoinOption {
  /** FK column on the target module that holds the source record's id */
  targetField: string;
  targetModule: ModuleTypeEnum;
  label: string;
}

/**
 * Well-known scalar FK columns per module, offered as one-click join
 * suggestions in the lookup builder. Tasks/time entries link to their parent
 * polymorphically via moduleId — offering PROJECT covers the common case
 * (non-project parents simply resolve to no match).
 */
export const COMMON_FOREIGN_KEYS: Partial<
  Record<ModuleTypeEnum, ForeignKeyOption[]>
> = {
  [ModuleTypeEnum.PROJECT]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
  ],
  [ModuleTypeEnum.TASK]: [
    { localField: 'moduleId', targetModule: ModuleTypeEnum.PROJECT, label: 'Project' },
  ],
  [ModuleTypeEnum.DEAL]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
    { localField: 'primaryContactId', targetModule: ModuleTypeEnum.CONTACT, label: 'Primary contact' },
  ],
  [ModuleTypeEnum.CONTACT]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
    { localField: 'supplierId', targetModule: ModuleTypeEnum.SUPPLIER, label: 'Supplier' },
  ],
  [ModuleTypeEnum.INVOICE]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
    { localField: 'projectId', targetModule: ModuleTypeEnum.PROJECT, label: 'Project' },
    { localField: 'contractId', targetModule: ModuleTypeEnum.CONTRACT, label: 'Contract' },
  ],
  [ModuleTypeEnum.CONTRACT]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
    { localField: 'projectId', targetModule: ModuleTypeEnum.PROJECT, label: 'Project' },
  ],
  [ModuleTypeEnum.TICKET]: [
    { localField: 'requesterEntityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Requester entity' },
    { localField: 'requesterContactId', targetModule: ModuleTypeEnum.CONTACT, label: 'Requester contact' },
  ],
  [ModuleTypeEnum.EVENT]: [
    { localField: 'entityId', targetModule: ModuleTypeEnum.ENTITY, label: 'Entity' },
  ],
  [ModuleTypeEnum.TIME_ENTRY]: [
    { localField: 'moduleId', targetModule: ModuleTypeEnum.PROJECT, label: 'Project' },
  ],
};

/**
 * Well-known one-to-many relationships per module, offered as one-click
 * suggestions in the rollup builder ("records that point at this one").
 * Tasks/time entries reference their parent polymorphically via moduleId.
 */
export const COMMON_REVERSE_JOINS: Partial<
  Record<ModuleTypeEnum, ReverseJoinOption[]>
> = {
  [ModuleTypeEnum.ENTITY]: [
    { targetField: 'entityId', targetModule: ModuleTypeEnum.PROJECT, label: 'Projects' },
    { targetField: 'entityId', targetModule: ModuleTypeEnum.DEAL, label: 'Deals' },
    { targetField: 'entityId', targetModule: ModuleTypeEnum.INVOICE, label: 'Invoices' },
    { targetField: 'entityId', targetModule: ModuleTypeEnum.CONTRACT, label: 'Contracts' },
    { targetField: 'entityId', targetModule: ModuleTypeEnum.CONTACT, label: 'Contacts' },
    { targetField: 'requesterEntityId', targetModule: ModuleTypeEnum.TICKET, label: 'Tickets' },
    { targetField: 'entityId', targetModule: ModuleTypeEnum.EVENT, label: 'Events' },
  ],
  [ModuleTypeEnum.PROJECT]: [
    { targetField: 'moduleId', targetModule: ModuleTypeEnum.TASK, label: 'Tasks' },
    { targetField: 'moduleId', targetModule: ModuleTypeEnum.TIME_ENTRY, label: 'Time entries' },
    { targetField: 'projectId', targetModule: ModuleTypeEnum.INVOICE, label: 'Invoices' },
    { targetField: 'projectId', targetModule: ModuleTypeEnum.CONTRACT, label: 'Contracts' },
  ],
  [ModuleTypeEnum.CONTRACT]: [
    { targetField: 'contractId', targetModule: ModuleTypeEnum.INVOICE, label: 'Invoices' },
  ],
  [ModuleTypeEnum.CONTACT]: [
    { targetField: 'primaryContactId', targetModule: ModuleTypeEnum.DEAL, label: 'Deals' },
    { targetField: 'requesterContactId', targetModule: ModuleTypeEnum.TICKET, label: 'Tickets' },
  ],
  [ModuleTypeEnum.SUPPLIER]: [
    { targetField: 'supplierId', targetModule: ModuleTypeEnum.CONTACT, label: 'Contacts' },
  ],
};
